// You can write more code here

/* START OF COMPILED CODE */

import FitTextToBounds from "../components/core/FitTextToBounds";
import CharacterBreathe from "../components/game/CharacterBreathe";
import UIButton from "../components/core/UIButton";
import DisableIfMobile from "../components/game/DisableIfMobile";
import LocaliseText from "../components/core/LocaliseText";
import TazGameReaction from "../components/game/TazGameReaction";
import Tutorial from "../prefabs/game/Tutorial";
/* START-USER-IMPORTS */
import { BaseScene } from '@scarybeasties/phaser-framework-core/scenes';
import { ParallaxController, MatterUpdater } from '@scarybeasties/phaser-framework-core/modules';
import { GameSceneData, SceneData, SceneId, TransitionPoint } from '../app-routing';
import { dynamicGameSceneAssetsToLoad } from '../assets-to-load';
import GameUI from './GameUI';
import { GameLayers } from './../game/GameLayers';
import { GameDataHandler } from '../game/GameDataHandler';
import { LevelManager } from '../game/LevelManager';
import { RunSettings, RunSettingsHandler } from '../game/RunSettingsHandler';
import {Level, LevelMapName, LevelName, LevelNameValues, Worlds} from '../game/levels/level';
import { LevelUtil } from '../game/levels/LevelUtil';
import { GameConfig } from "../game/config/gameConfig";
import Vector2 = Phaser.Math.Vector2;
import Powerup from "../game/powerups/Powerup";
import Tile from "../game/Tile";
import ScoreMultiplierNotification from "../prefabs/game/ScoreMultiplierNotification";
import BlocksBrokenNotification from "../prefabs/game/BlocksBrokenNotification";
import Critter from "../prefabs/tiles/Critter";
import Taz from "../prefabs/tiles/Taz";
import {TutorialSteps} from "../prefabs/game/Tutorial";

import assetPackCountry from '../../../static/asset-packs/asset-pack-game-country.json';
import assetPackDesert from '../../../static/asset-packs/asset-pack-game-desert.json';
import assetPackSpace from '../../../static/asset-packs/asset-pack-game-space.json';
import assetPackIce from '../../../static/asset-packs/asset-pack-game-ice.json';
import { Sounds } from "../sounds";

export const GameState = {
  None: 'None',
  Drag: 'Drag',
  SpinningTaz: 'SpinningTaz',
  GameComplete: 'GameComplete',
} as const;
export type GameStateKeys = keyof typeof GameState;
export type GameStateValues = typeof GameState[GameStateKeys];
/* END-USER-IMPORTS */

export default class Game extends BaseScene {

  constructor() {
    super("Game");

    /* START-USER-CTR-CODE */
    this.hasGameCreateFinished = false;
    this.hasGameUICreateFinished = false;
    /* END-USER-CTR-CODE */
  }

  editorCreate(): void {

    // BackgroundParallaxLayer
    const backgroundParallaxLayer = this.add.layer();

    // ActionLayer
    const actionLayer = this.add.layer();

    // ForegroundParallaxLayer
    const foregroundParallaxLayer = this.add.layer();

    // UILayer
    const uILayer = this.add.layer();

    // SpinMeter
    const spinMeter = this.add.container(473, 111);
    uILayer.add(spinMeter);

    // circle_white_large_png_2
    const circle_white_large_png_2 = this.add.image(38, -134, "game-tiles", "spinometer-arm.png");
    spinMeter.add(circle_white_large_png_2);

    // bg
    const bg = this.add.sprite(0, 105, "game-tiles", "spinometer.png");
    spinMeter.add(bg);

    // SpinMeterBar
    const spinMeterBar = this.add.sprite(0, 105, "game-tiles", "spinometer-arrow.png");
    spinMeterBar.angle = -90;
    spinMeterBar.setOrigin(0.5, 1);
    spinMeter.add(spinMeterBar);

    // livesBox
    const livesBox = this.add.sprite(0, 130, "game-tiles", "spinometer-lives.png");
    spinMeter.add(livesBox);

    // LivesLabel
    const livesLabel = this.add.text(0, 161, "", {});
    livesLabel.angle = -1;
    livesLabel.setOrigin(0.5, 0.5);
    livesLabel.text = "000";
    livesLabel.setStyle({ "align": "center", "fontFamily": "Acme-Regular", "fontSize": "30px" });
    spinMeter.add(livesLabel);

    // scoreLabelBox
    const scoreLabelBox = this.add.container(2.753814697265625, -51.6966667175293);
    scoreLabelBox.angle = 4;
    spinMeter.add(scoreLabelBox);

    // scoreLabelRect
    const scoreLabelRect = this.add.sprite(0, 0, "game-tiles", "smashbar-score.png");
    scoreLabelBox.add(scoreLabelRect);

    // ScoreLabel
    const scoreLabel = this.add.text(0.999973420821334, 1.9999958959580226, "", {});
    scoreLabel.setOrigin(0.5, 0.5);
    scoreLabel.tintFill = true;
    scoreLabel.tintTopLeft = 58827;
    scoreLabel.tintTopRight = 58827;
    scoreLabel.tintBottomLeft = 58827;
    scoreLabel.tintBottomRight = 58827;
    scoreLabel.text = "000";
    scoreLabel.setStyle({ "align": "center", "fontFamily": "Acme-Regular", "fontSize": "50px", "stroke": "" });
    scoreLabelBox.add(scoreLabel);

    // ScoreMultiplierSmallLabel
    const scoreMultiplierSmallLabel = this.add.text(58, -30, "", {});
    scoreMultiplierSmallLabel.angle = 20;
    scoreMultiplierSmallLabel.setOrigin(0.5, 0.5);
    scoreMultiplierSmallLabel.text = "x2";
    scoreMultiplierSmallLabel.setStyle({ "align": "center", "color": "#ffff00", "fontFamily": "Acme-Regular", "fontSize": "25px" });
    scoreLabelBox.add(scoreMultiplierSmallLabel);

    // teammate-sprite
    const teammate_sprite = this.add.sprite(1404, 787, "game-tiles", "teammate-marvinmartian.png");
    teammate_sprite.setOrigin(0.5, 1);
    uILayer.add(teammate_sprite);

    // SmashBar
    const smashBar = this.add.container(900, -359);
    smashBar.angle = -0.5;
    uILayer.add(smashBar);

    // circle_white_large_png_3
    const circle_white_large_png_3 = this.add.image(22, 208, "game-tiles", "smashbar-arm.png");
    smashBar.add(circle_white_large_png_3);

    // smashBarBG
    const smashBarBG = this.add.sprite(72, 404, "game-tiles", "smashbar.png");
    smashBar.add(smashBarBG);

    // smashBarMeter
    const smashBarMeter = this.add.sprite(-197, 404, "game-tiles", "smashbar-fill.png");
    smashBarMeter.setOrigin(0, 0.5);
    smashBar.add(smashBarMeter);

    // unlockNextLevelContainer
    const unlockNextLevelContainer = this.add.container(106.99995032104925, 386);
    smashBar.add(unlockNextLevelContainer);

    // unlockNextLevel_1
    const unlockNextLevel_1 = this.add.sprite(0, 0, "game-tiles", "smashbar-icon-clear-arrow.png");
    unlockNextLevel_1.visible = false;
    unlockNextLevelContainer.add(unlockNextLevel_1);

    // unlockNextLevel
    const unlockNextLevel = this.add.sprite(0, 35, "game-tiles", "smashbar-icon-clear.png");
    unlockNextLevelContainer.add(unlockNextLevel);

    // star1
    const star1 = this.add.sprite(-197, 386, "game-tiles", "smashbar-star.png");
    smashBar.add(star1);

    // star2
    const star2 = this.add.sprite(38, 386, "game-tiles", "smashbar-star.png");
    smashBar.add(star2);

    // star3
    const star3 = this.add.sprite(255, 386, "game-tiles", "smashbar-star.png");
    smashBar.add(star3);

    // teammate
    const teammate = this.add.container(1372, 730);
    uILayer.add(teammate);

    // PowerupButton
    const powerupButton = this.add.container(99, -28);
    teammate.add(powerupButton);

    // circle_white_large_png
    const circle_white_large_png = this.add.image(0, 0, "common", "btn-top-ui-right-green.png");
    powerupButton.add(circle_white_large_png);

    // circle_white_large_png_1
    const circle_white_large_png_1 = this.add.image(0, 0, "common", "icon-teamate-power.png");
    powerupButton.add(circle_white_large_png_1);

    // key-powerup
    const key_powerup = this.add.image(48, 41, "game-tiles", "btn-keyboard-blank.png");
    key_powerup.scaleX = 0.8;
    key_powerup.scaleY = 0.8;
    powerupButton.add(key_powerup);

    // key-label-powerup
    const key_label_powerup = this.add.text(48, 42, "", {});
    key_label_powerup.setOrigin(0.5, 0.5);
    key_label_powerup.text = "F";
    key_label_powerup.setStyle({ "color": "#000000ff", "fontFamily": "Acme-Regular", "fontSize": "30px" });
    powerupButton.add(key_label_powerup);

    // HitAreaMobileRect
    const hitAreaMobileRect = this.add.image(-10, -127, "_MISSING");
    hitAreaMobileRect.scaleX = 10;
    hitAreaMobileRect.scaleY = 12;
    hitAreaMobileRect.alpha = 0;
    hitAreaMobileRect.alphaTopLeft = 0;
    hitAreaMobileRect.alphaTopRight = 0;
    hitAreaMobileRect.alphaBottomLeft = 0;
    hitAreaMobileRect.alphaBottomRight = 0;
    powerupButton.add(hitAreaMobileRect);

    // powerup_1
    const powerup_1 = this.add.image(60, -87, "game-tiles", "counter-pink.png");
    teammate.add(powerup_1);

    // powerup_2
    const powerup_2 = this.add.image(98, -87, "game-tiles", "counter-pink.png");
    teammate.add(powerup_2);

    // powerup_3
    const powerup_3 = this.add.image(136, -87, "game-tiles", "counter-pink.png");
    teammate.add(powerup_3);

    // taz
    const taz = this.add.container(0, 0);
    uILayer.add(taz);

    // taz-screen-character
    const taz_screen_character = this.add.sprite(463, 768, "game-tiles", "taz-1.png");
    taz_screen_character.setOrigin(0.5, 1);
    taz.add(taz_screen_character);

    // BumperButton
    const bumperButton = this.add.container(443, 702);
    taz.add(bumperButton);

    // bumper-button-bg
    const bumper_button_bg = this.add.image(0, 0, "common", "btn-top-ui-left-green.png");
    bumperButton.add(bumper_button_bg);

    // bumper-button-icon
    const bumper_button_icon = this.add.image(-2, -7, "common", "icon-bumper.png");
    bumperButton.add(bumper_button_icon);

    // key-bumper-button
    const key_bumper_button = this.add.image(-2, 39, "game-tiles", "btn-keyboard-space.png");
    key_bumper_button.scaleX = 0.8;
    key_bumper_button.scaleY = 0.8;
    bumperButton.add(key_bumper_button);

    // HitAreaMobileBumperRect
    const hitAreaMobileBumperRect = this.add.image(-6, -13, "_MISSING");
    hitAreaMobileBumperRect.scaleX = 10;
    hitAreaMobileBumperRect.scaleY = 10;
    hitAreaMobileBumperRect.alpha = 0;
    hitAreaMobileBumperRect.alphaTopLeft = 0;
    hitAreaMobileBumperRect.alphaTopRight = 0;
    hitAreaMobileBumperRect.alphaBottomLeft = 0;
    hitAreaMobileBumperRect.alphaBottomRight = 0;
    bumperButton.add(hitAreaMobileBumperRect);

    // scoreMultiplier
    const scoreMultiplier = this.add.container(0, 0);
    uILayer.add(scoreMultiplier);

    // ScoreMultiplierText
    const scoreMultiplierText = this.add.text(960, 384, "", {});
    scoreMultiplierText.setOrigin(0.5, 0.5);
    scoreMultiplierText.text = "x2";
    scoreMultiplierText.setStyle({ "align": "center", "color": "#ffff00", "fontFamily": "Acme-Regular", "fontSize": "150px" });
    scoreMultiplier.add(scoreMultiplierText);

    // finalSpin
    const finalSpin = this.add.container(960, 384);
    finalSpin.visible = false;
    uILayer.add(finalSpin);

    // FinalSpinText
    const finalSpinText = this.add.text(0, 0, "", {});
    finalSpinText.setOrigin(0.5, 0.5);
    finalSpinText.text = "Final Spin!";
    finalSpinText.setStyle({ "align": "center", "color": "#ffff00", "fontFamily": "Acme-Regular", "fontSize": "150px", "shadow.offsetY":3,"shadow.fill":true});
    finalSpin.add(finalSpinText);

    // tutorialGuide
    const tutorialGuide = this.add.container(960, 384);

    // left
    const left = this.add.rectangle(0, 0, 1920, 768);
    left.isFilled = true;
    left.fillColor = 0;
    left.fillAlpha = 0.7;
    tutorialGuide.add(left);

    // tutorial
    const tutorial = new Tutorial(this, 0, 0);
    this.add.existing(tutorial);

    // tutorialMask
    const tutorialMask = this.add.image(960, 384, "game-tiles", "tutorial-mask.png");

    // lists
    const stars = [star3, star2, star1];
    const powerups = [powerup_1, powerup_2, powerup_3];

    // scoreLabel (components)
    const scoreLabelFitTextToBounds = new FitTextToBounds(scoreLabel);
    scoreLabelFitTextToBounds.maxWidth = 100;
    scoreLabelFitTextToBounds.constrainByWidth = true;

    // teammate_sprite (components)
    new CharacterBreathe(teammate_sprite);

    // powerupButton (components)
    const powerupButtonUIButton = new UIButton(powerupButton);
    powerupButtonUIButton.mobileVisibleList = [hitAreaMobileRect];

    // key_powerup (components)
    new DisableIfMobile(key_powerup);

    // key_label_powerup (components)
    new DisableIfMobile(key_label_powerup);
    const key_label_powerupLocaliseText = new LocaliseText(key_label_powerup);
    key_label_powerupLocaliseText.copyId = "powerupKey";

    // taz_screen_character (components)
    new TazGameReaction(taz_screen_character);
    new CharacterBreathe(taz_screen_character);

    // bumperButton (components)
    const bumperButtonUIButton = new UIButton(bumperButton);
    bumperButtonUIButton.mobileVisibleList = [hitAreaMobileBumperRect];

    // key_bumper_button (components)
    new DisableIfMobile(key_bumper_button);

    // finalSpinText (components)
    const finalSpinTextFitTextToBounds = new FitTextToBounds(finalSpinText);
    finalSpinTextFitTextToBounds.maxWidth = 800;
    finalSpinTextFitTextToBounds.constrainByWidth = true;
    const finalSpinTextLocaliseText = new LocaliseText(finalSpinText);
    finalSpinTextLocaliseText.copyId = "finalSpin";

    this.backgroundParallaxLayer = backgroundParallaxLayer;
    this.actionLayer = actionLayer;
    this.foregroundParallaxLayer = foregroundParallaxLayer;
    this.spinMeter = spinMeter;
    this.spinMeterBar = spinMeterBar;
    this.livesLabel = livesLabel;
    this.scoreLabelBox = scoreLabelBox;
    this.scoreLabel = scoreLabel;
    this.scoreMultiplierSmallLabel = scoreMultiplierSmallLabel;
    this.teammate_sprite = teammate_sprite;
    this.smashBar = smashBar;
    this.smashBarMeter = smashBarMeter;
    this.unlockNextLevelContainer = unlockNextLevelContainer;
    this.unlockNextLevel_1 = unlockNextLevel_1;
    this.unlockNextLevel = unlockNextLevel;
    this.teammate = teammate;
    this.powerupButton = powerupButton;
    this.taz_screen_character = taz_screen_character;
    this.bumperButton = bumperButton;
    this.scoreMultiplierText = scoreMultiplierText;
    this.finalSpin = finalSpin;
    this.finalSpinText = finalSpinText;
    this.tutorialGuide = tutorialGuide;
    this.tutorial = tutorial;
    this.tutorialMask = tutorialMask;
    this.stars = stars;
    this.powerups = powerups;

    this.events.emit("scene-awake");
  }

  private backgroundParallaxLayer!: Phaser.GameObjects.Layer;
  private actionLayer!: Phaser.GameObjects.Layer;
  private foregroundParallaxLayer!: Phaser.GameObjects.Layer;
  private spinMeter!: Phaser.GameObjects.Container;
  private spinMeterBar!: Phaser.GameObjects.Sprite;
  private livesLabel!: Phaser.GameObjects.Text;
  private scoreLabelBox!: Phaser.GameObjects.Container;
  private scoreLabel!: Phaser.GameObjects.Text;
  private scoreMultiplierSmallLabel!: Phaser.GameObjects.Text;
  private teammate_sprite!: Phaser.GameObjects.Sprite;
  private smashBar!: Phaser.GameObjects.Container;
  private smashBarMeter!: Phaser.GameObjects.Sprite;
  private unlockNextLevelContainer!: Phaser.GameObjects.Container;
  private unlockNextLevel_1!: Phaser.GameObjects.Sprite;
  private unlockNextLevel!: Phaser.GameObjects.Sprite;
  private teammate!: Phaser.GameObjects.Container;
  private powerupButton!: Phaser.GameObjects.Container;
  private taz_screen_character!: Phaser.GameObjects.Sprite;
  private bumperButton!: Phaser.GameObjects.Container;
  private scoreMultiplierText!: Phaser.GameObjects.Text;
  private finalSpin!: Phaser.GameObjects.Container;
  private finalSpinText!: Phaser.GameObjects.Text;
  private tutorialGuide!: Phaser.GameObjects.Container;
  private tutorial!: Tutorial;
  private tutorialMask!: Phaser.GameObjects.Image;
  private stars!: Phaser.GameObjects.Sprite[];
  private powerups!: Phaser.GameObjects.Image[];

  /* START-USER-CODE */
  private sceneData!: SceneData;
  private gameSceneData!: GameSceneData;
  private runSettings!: RunSettings;
  private gameUI!: GameUI;
  private levelManager!: LevelManager;
  private parallaxController!: ParallaxController;
  private gameLayers!: GameLayers;

  protected hasGameCreateFinished: boolean;
  protected hasGameUICreateFinished: boolean;
  protected level!: Level;
  private player!:Taz;
  private powerLine!:Phaser.GameObjects.Graphics;
  private arrowLine!:Phaser.GameObjects.Graphics;
  private playerCircle!:Phaser.GameObjects.Graphics;
  private gameState!: GameStateKeys;
  private availableSpins: integer = 0;
  public currentPowerup!:Powerup | undefined;
  private availablePowerups : integer = 0;
  private scoreMultiplierDuration:number = 0;
  private scoreMultiplier:number = 1;
  private scoreNotifications: ScoreMultiplierNotification[] = [];
  private blocksBrokenNotification: BlocksBrokenNotification[] = [];
  private hasNotifiedFinalSpin:boolean = false;

  // Allows game to wait for gameUI to be ready.
  set HasGameUICreateFinished(hasFinished: boolean) {
    this.hasGameUICreateFinished = hasFinished;
  }

  get Level() {
    return this.level;
  }


  public init(data: SceneData) {
    super.init(data);

    let gameSceneDataDefaults: GameSceneData = {
      shouldSkip: false
    };

    let runSettingsDefaults: RunSettings = {
      selectedLevel: LevelName.Level1,
      instantRestart: false,
      regenOnRestart: true
    };



    this.sceneData = data;
    this.gameSceneData = Object.assign({}, gameSceneDataDefaults, data.gameSceneData);
    this.runSettings = Object.assign({}, runSettingsDefaults, data.runSettings);

    if(data.level !== undefined)
    {
      this.runSettings.selectedLevel = "level"+ data.level as LevelNameValues;
    }

    this.registry.set(GameDataHandler.keys.Score, 0);
    this.registry.set(GameDataHandler.keys.BlocksBroken,0);

    this.hasGameCreateFinished = false;
    this.hasGameUICreateFinished = false;
    this.hasNotifiedFinalSpin = false;

    GameDataHandler.updateLastLevelPlayed(this, this.runSettings.selectedLevel);
    this.level = LevelUtil.getLevelFromName(this.runSettings.selectedLevel);
    this.availableSpins = this.level.lives;

    let dynamicAssetsToPreload = dynamicGameSceneAssetsToLoad(this);
    this.app.App.AssetManager.addDynamicAssets(dynamicAssetsToPreload);
    this.changeState(GameState.None);

    this.availablePowerups = GameConfig.availableAmountOfPowerups;

    this.events.on(LevelManager.events.UpdateScore,this.updateScore,this);
    this.events.on(LevelManager.events.BlockBroken,this.updateBlocksBroken,this);

    this.app.AudioHandler.playSound(this, this.level.bgMusic, Sounds.Channel.Music, {
      volume: 0.1,
      loop: true
    });

  }

  public preload() {
    super.preload();

    // this.load.pack('asset-pack-game', assetPack as any);
    this.load.pack(Worlds[GameDataHandler.getWorldForLevel(this,this.runSettings.selectedLevel)].assetPack,this.getAssetPackToLoad() as any);

    this.load.on(Phaser.Loader.Events.COMPLETE, this.postload, this);
  }

  public getAssetPackToLoad()
  {
    let pack = Worlds[GameDataHandler.getWorldForLevel(this,this.runSettings.selectedLevel)].assetPack;
    if(pack == "asset-pack-game-desert")
    {
      return assetPackDesert;
    }
    if(pack == "asset-pack-game-country")
    {
      return assetPackCountry;
    }
    if(pack == "asset-pack-game-space")
    {
      return assetPackSpace;
    }
    if(pack == "asset-pack-game-ice")
    {
      return assetPackIce;
    }
  }

  public postload() {
    super.postload();
  }

  public create() {
    super.create();
    this.editorCreate();

    this.gameLayers = {
      backgroundLayer: this.backgroundParallaxLayer,
      actionLayer: this.actionLayer,
      foregroundLayer: this.foregroundParallaxLayer
    };

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
    this.events.once(Phaser.Scenes.Events.DESTROY, this.destroy, this);
    this.events.on(Tutorial.events.pausableTutorialStepStarted,this.pauseForTutorial,this);
    this.events.on(Tutorial.events.tutorialStepCompleted,this.tutorialStepCompleted,this);
    this.events.on(Tutorial.events.idlePrompt,this.idlePrompt,this);

    this.livesLabel.text = this.availableSpins+"";

    this.tutorialGuide.visible = false;


    this.spinMeter.y -= 500;
    this.teammate.y += 200;
    this.smashBar.y -= 200;

    this.showGameUI();

    this.setupGame();

    this.hasGameCreateFinished = true;
    this.start();
    this.setupSmashBar();

    this.teammate_sprite.setTexture(this.teammate_sprite.texture.key,"teammate-"+GameDataHandler.getLastCharacterPlayed(this).toLowerCase()+".png");
    this.scoreMultiplierText.visible = false;
    this.scoreMultiplierSmallLabel.visible = false;

    this.tutorial.setupTutorial(this,this.player, this.powerupButton, this.bumperButton, this.tutorialGuide,this.tutorialMask);


    UIButton.getComponent(this.powerupButton).disableButton();
  }

  private setupSmashBar()
  {
    for(let i = 0; i< this.stars.length; ++i)
    {
      this.stars[i].x = this.smashBarMeter.x+(this.smashBarMeter.width*this.level.smashLevels.smashLevels[i]/100);
      this.stars[i].tint = 0x7f7f7c;
      this.stars[i].scale = 0.65;
    }

    this.unlockNextLevelContainer.x = this.smashBarMeter.x+(this.smashBarMeter.width*(this.level.targetScore/100));
    this.unlockNextLevel.tint = 0x7f7f7c;
    this.unlockNextLevel.scale = 0.65;
  }

  private changeState(target:GameStateKeys)
  {
    this.gameState = target;

    switch (this.gameState)
    {
      case GameState.Drag:
        this.levelManager.resetBumpers();
        this.updatedBumper = false;
        this.scoreMultiplierDuration = 0;
        break;
    }
  }


  public start() {
    if (!this.hasGameCreateFinished || !this.hasGameUICreateFinished) return;

    this.startGame();
  }

  public runPowerup()
  {
    console.log("trying to run powerup ",this.gameState, this.availablePowerups, this.currentPowerup);
    if(this.gameState == GameState.SpinningTaz) {
      if (this.availablePowerups > 0 && this.currentPowerup == undefined) {
        this.currentPowerup = LevelUtil.getPowerup(GameDataHandler.getLastCharacterPlayed(this),this,0,0);
        if(this.currentPowerup != undefined) {
          this.add.existing(this.currentPowerup);
          this.gameLayers.foregroundLayer.add(this.currentPowerup);
          this.currentPowerup.ApplyPowerup(this.levelManager);
          this.availablePowerups--;
          this.powerups[this.availablePowerups].visible = false;

          if(this.currentPowerup.AffectsScore())
          {
            this.showScoreMultiplier(this.currentPowerup.multiplier,false);
            console.log("What is the duration of multiplier ", this.currentPowerup.duration);
            this.scoreMultiplierDuration = this.currentPowerup.duration;
            this.scoreMultiplier = this.currentPowerup.multiplier;
          }
        }

        this.tutorial.completeTutorialStep(TutorialSteps.PressPowerup);

        let characterSFX:string = GameDataHandler.getLastCharacterPlayed(this).toLowerCase() + "_powerup";
        this.app.AudioHandler.playSound(this, characterSFX, Sounds.Channel.SFX, {
          volume:3
        });

        UIButton.getComponent(this.powerupButton).disableButton();
        // this.powerupButton.disableInteractive();

        this.teammate_sprite.angle = 5;
        const chain = this.tweens.chain({
          targets: this.teammate_sprite,
          tweens: [
              {
                  angle:-5,
                  ease: Phaser.Math.Easing.Quadratic.InOut,
                  duration: 100
              },
              {
                angle:2,
                ease: Phaser.Math.Easing.Quadratic.InOut,
                duration: 100
              },
              {
                angle:-1,
                ease: Phaser.Math.Easing.Quadratic.InOut,
                duration: 100
              },
              {
                angle:0,
                ease: Phaser.Math.Easing.Quadratic.InOut,
                duration: 100
              }
          ]
      });
      }
    }
  }

  protected showGameUI() {
    this.scene.run(SceneId.GameUI, this.sceneData);
    this.gameUI = this.scene.get(SceneId.GameUI) as GameUI;
    this.gameUI.events.once(Phaser.Scenes.Events.CREATE, this.gameUI.showGameUI, this.gameUI);
  }

  // One time setup for the scene.
  protected setupGame() {

    let zoom = this.game.device.os.desktop ? 1 : 1;
    this.cameras.main.setZoom(zoom, zoom);

    this.cameras.main.setBackgroundColor(0xff7f11);

    this.playerCircle = this.add.graphics({x:0,y:0});
    this.gameLayers.actionLayer.add(this.playerCircle);

    this.levelManager = new LevelManager(this, this.level);

    this.levelManager.init(this.cameras.main, this.gameLayers);

    this.player = this.levelManager.Player;
    this.player.gameEngine = this;
    this.events.on(Taz.events.MovementComplete,this.PlayerMovementEnded,this);
    this.events.on(Taz.events.SpeedBoost,this.PlayerSpeedBoost,this);

    this.powerupButton.on(UIButton.events.clicked,this.runPowerup,this);
    UIButton.getComponent(this.powerupButton).bindToKeyPress("keydown-F","keyup-F");

    if(this.levelManager.hasBumpers())
    {
      this.bumperButton.on(UIButton.events.pressed,this.bumperButtonPressed,this);
      this.bumperButton.on(UIButton.events.clicked,this.bumperButtonReleased,this);

      UIButton.getComponent(this.bumperButton).bindToKeyPress("keydown-SPACE","keyup-SPACE");

    }
    else
    {
      this.bumperButton.visible = false;
    }

    this.input.on(Phaser.Input.Events.POINTER_DOWN, () => {
      this.checkIfMouseOnPlayer();

    });
    this.input.on(Phaser.Input.Events.POINTER_UP, () => {
      this.mouseUp();
    });

    this.input.on(Phaser.Input.Events.POINTER_UP_OUTSIDE, () => {
      this.mouseUp();
    });

    this.powerLine = this.add.graphics({x:0,y:0});
    this.arrowLine = this.add.graphics({x:0,y:0});
  }

  private mouseUp()
  {
    if(this.canReSpinTaz()) {
      if (this.mouseDown) {

        let offset = this.cameras.main.width-1920;
        let p: Vector2 = new Phaser.Math.Vector2(this.player.x, this.player.y);
        let p1: Vector2 = new Phaser.Math.Vector2(this.input.activePointer.x - offset / 2, this.input.activePointer.y);
        let centerDist = p.distance(p1);

        if(centerDist > 65)
        {
          this.player.applyForce();
          this.tutorial.completeTutorialStep(TutorialSteps.MovePlayerInMotion);

          this.useAvailableSpin();
          this.changeState(GameState.SpinningTaz);
          TazGameReaction.getComponent(this.taz_screen_character).Reset();
        }
        this.powerLine.clear();
          this.arrowLine.clear();
          this.playerCircle.clear();
      }
    }
    // this.updateBumpers();
    this.mouseDown = false;
  }

  protected startGame() {
    //this.LoadTileMap();
    this.changeState(GameState.Drag);

    this.add.tween({
      targets: this.spinMeter,
      y: "+=500",
      duration: 500,
      delay: 600,
      ease: "Back.Out"
    });

    this.add.tween({
      targets: this.smashBar,
      y:-360,
      duration: 500,
      delay: 500,
      ease: "Back.Out"
    });

    this.add.tween({
      targets: this.teammate,
      y: "-=200",
      duration: 200,
      delay: 700,
      ease: "Quad.InOut"
    });

    this.tutorial.startTutorialStep(this.level.tutorialStep);
  }

  public canReSpinTaz()
  {
    return this.availableSpins > 0 && this.player.velocity < GameConfig.minSpeedBeforeAllowingNewSpin;
  }

  public showFinalSpin()
  {
    this.finalSpin.visible = true;
    this.finalSpin.alpha = 0;
    this.finalSpin.scale = 1.1;
    this.add.tween({
      targets:this.finalSpin,
      alpha:1,
      scale:1,
      duration:300,
      ease:Phaser.Math.Easing.Quadratic.InOut
    })

    this.add.tween({
      targets:this.finalSpin,
      alpha:0,
      scale:0.9,
      duration:300,
      delay:1000,
      ease:Phaser.Math.Easing.Quadratic.InOut
    })
  }

  private pausedForTutorial:boolean = false;
  private pauseForTutorial()
  {
    this.pausedForTutorial = true;
    UIButton.getComponent(this.powerupButton).enableButton();
  }

  private tutorialStepCompleted()
  {

    this.pausedForTutorial = false;
  }

  private idlePrompt()
  {
    TazGameReaction.getComponent(this.taz_screen_character).ReactIdle();
  }

  public mouseDown:boolean = false;
  public update(time: number, delta: number) {

    this.tutorial.update(time,delta);
    if(!this.pausedForTutorial)
    {

      if(this.canReSpinTaz())
      {
        this.updateCursor();
        if(this.player.velocity > 10)
        {
          TazGameReaction.getComponent(this.taz_screen_character).ReactSlowDown();


          if(this.availableSpins == 1 && !this.hasNotifiedFinalSpin)
          {
            this.hasNotifiedFinalSpin = true;
            this.showFinalSpin();
          }
        }
      }
      else{
        this.powerLine.clear();
        this.arrowLine.clear();
        this.playerCircle.clear();
      }

      switch (this.gameState) {
        case GameState.Drag:
          UIButton.getComponent(this.bumperButton).disableButton();
          UIButton.getComponent(this.powerupButton).disableButton();
          break;
        case GameState.SpinningTaz:
          this.player.movePlayer(delta);
          if(this.currentPowerup == undefined && this.availablePowerups > 0)
          {
            UIButton.getComponent(this.powerupButton).enableButton();
          }
          else
          {
            UIButton.getComponent(this.powerupButton).disableButton();
          }
          UIButton.getComponent(this.bumperButton).enableButton();


          this.updateSpinMeter();
          // this.updateBumpers();
          this.updateScoreMultiplier(delta);
          break;
      }

      this.levelManager.updateLevel(time, delta);
    }
    if(this.currentPowerup !== undefined)
    {
      this.currentPowerup.update(this.player.x,this.player.y, time,delta);
      if(this.currentPowerup.HasBeenDestroyed())
      {
        this.currentPowerup = undefined;
      }
    }


  }

  private checkIfMouseOnPlayer()
  {
    if(this.canReSpinTaz()) {
      let offset = this.cameras.main.width - 1920;
      let p1: Vector2 = new Phaser.Math.Vector2(this.input.activePointer.x - offset / 2, this.input.activePointer.y);
      if (p1.x < this.levelManager.mapRightBorder && p1.x > this.levelManager.mapLeftBorder && p1.y > this.levelManager.mapTopBorder && p1.y < this.levelManager.mapBottomBorder)
      {
        this.mouseDown = true;
        this.levelManager.resetBumpers();
        this.updatedBumper = false;


      }
    }
  }

  private updateCursor()
  {
    if(this.mouseDown)
    {
      let offset = this.cameras.main.width-1920;
      let p: Vector2 = new Phaser.Math.Vector2(this.player.x, this.player.y);
      let dir: Vector2 = new Phaser.Math.Vector2(this.input.activePointer.x - offset / 2, this.input.activePointer.y);
      let p1: Vector2 = new Phaser.Math.Vector2(this.input.activePointer.x - offset / 2, this.input.activePointer.y);

      dir.subtract(p);
      dir.normalize();

      let centerDist = p.distance(p1);
      p.add(dir.setLength(45));

      let dist = p.distance(p1)-20;


      this.playerCircle.clear();
      this.playerCircle.fillStyle(0x000000,0.3);
      this.playerCircle.fillCircle(this.player.x,this.player.y,35);

      console.log(centerDist);
      if(centerDist < 65)
      {
        this.powerLine.clear();
        this.arrowLine.clear();
      }
      else{
          this.tutorial.completeTutorialStep(TutorialSteps.MovePlayer);

        if(dist > GameConfig.maxLineLength)
        {
          dir.setLength(GameConfig.maxLineLength);
          p1 = dir.add(p);
        }
        else
        {
          dir.setLength(dist);
          p1 = dir.add(p);
        }

        this.powerLine.clear();
        this.powerLine.lineStyle(5,this.level.arrowColor,1);
        this.powerLine.fillStyle(this.level.arrowColor);

        let stepAmount = 20;
        let l = new Vector2(p1.x,p1.y).distance(new Vector2(p.x,p.y))/stepAmount;
        for(let i = 0; i < l; ++i)
        {
          let x = this.lerp(p.x,p1.x,i/l);
          let y = this.lerp(p.y,p1.y,i/l);
          let dir2:Vector2 = new Vector2(p1.x,p1.y).subtract(new Vector2(p.x,p.y)).normalize();
          let s = new Vector2(x,y).add(dir2.setLength(stepAmount/2));

          this.powerLine.lineBetween(x,y,s.x,s.y);
        }

        // this.powerLine.lineBetween(p.x,p.y,p1.x,p1.y);

        this.arrowLine.clear();
        this.arrowLine.fillStyle(this.level.arrowColor);
        this.arrowLine.x = p1.x;
        this.arrowLine.y = p1.y;
        this.arrowLine.fillTriangle(-10, 0, 10, 0, 0, 20);
        this.arrowLine.angle = Phaser.Math.Angle.Between(p.x,p.y,p1.x,p1.y)*(180/Math.PI)-90;
      }
    }
  }

  private useAvailableSpin()
  {
    this.availableSpins --;
    this.livesLabel.text = this.availableSpins+"";


  }

  private updatedBumper:boolean = false;
  private bumperButtonPressed()
  {
    if(this.gameState != GameState.SpinningTaz)
    {
      return;
    }

    if(!this.updatedBumper) {
      this.levelManager.toggleBumpers();
      this.updatedBumper = true;
    }
    this.tutorial.completeTutorialStep(TutorialSteps.PressBumper);
  }

  private bumperButtonReleased()
  {
    if(this.updatedBumper) {
      this.levelManager.toggleBumpers();
      this.updatedBumper = false;
    }
  }

  private updateSpinMeter()
  {
    let speed = Math.abs(this.player.vx + this.player.vy);

    this.spinMeterBar.angle = this.lerp(this.spinMeterBar.angle, 180*(speed/GameConfig.playerMaxVelocity)-90,0.1);
    this.spinMeterBar.angle = Math.min(this.spinMeterBar.angle, 90);
    this.spinMeterBar.angle = Math.max(this.spinMeterBar.angle, -90);
  }

  private lerp (start:number, end:number, amt:number){
    return (1-amt)*start+amt*end
  }

  public PlayerMovementEnded()
  {
    if(this.availableSpins <= 0 || this.levelManager.destroyedAllTiles())
    {
      this.changeState(GameState.GameComplete);
      this.finish();
    }
    else {
      this.changeState(GameState.Drag);
    }
  }

  public PlayerSpeedBoost()
  {
    this.spinMeter.angle = 10;
    const chain = this.tweens.chain({
      targets: this.spinMeter,
      tweens: [
          {
              angle:-10,
              ease: Phaser.Math.Easing.Quadratic.InOut,
              duration: 100
          },
          {
            angle:8,
            ease: Phaser.Math.Easing.Quadratic.InOut,
            duration: 100
          },
          {
            angle:-5,
            ease: Phaser.Math.Easing.Quadratic.InOut,
            duration: 100
          },
          {
            angle:2,
            ease: Phaser.Math.Easing.Quadratic.InOut,
            duration: 100
          },
          {
            angle:0,
            ease: Phaser.Math.Easing.Quadratic.InOut,
            duration: 100
          }
      ]
  });
  }

  private updateScoreMultiplier(delta:number)
  {
    if(this.scoreMultiplierDuration > 0)
    {
      this.scoreMultiplierDuration -= delta;
    }
    else
    {
      if(this.scoreBoxTween != undefined)
      {
        this.scoreBoxTween.stop();
        this.scoreBoxTween = undefined;
        this.scoreLabelBox.scale = 1;
        this.scoreLabel.tint = 0x00e5cb;
        this.scoreMultiplierSmallLabel.visible = false;
      }
      this.scoreMultiplier = 1;
    }


  }

  private updateScore(tile:Tile)
  {
    if(tile != null) {
      let tileScore = tile.Score;
      let multiplier = this.scoreMultiplier;
      let currentlyMultiplyingScore: boolean = this.scoreMultiplier > 1;
      if (this.currentPowerup !== undefined && this.currentPowerup.AffectsScore()) {
        tileScore = this.currentPowerup.modifyScore(tileScore);
        // multiplier *= this.currentPowerup.multiplier;
      }

      if(tile.canMultiplyScore())
      {
        if(tile.scoreMultiplerDuration*1000 > this.scoreMultiplierDuration)
        {
          this.scoreMultiplierDuration = tile.scoreMultiplerDuration*1000;
        }
        this.scoreMultiplier += tile.scoreMultiplier-1;
        // if(!currentlyMultiplyingScore)
        // {
          this.showScoreMultiplier(this.scoreMultiplier);
        // }
      }

      // multiplier *= this.scoreMultiplier; 


      if(multiplier > 1)
      {
        let notification = this.getNewScoreNotification(tile.x,tile.y);
        notification.show(this.scoreMultiplier);

      }

      tileScore *= this.scoreMultiplier;

      this.registry.inc(GameDataHandler.keys.Score, tileScore);

    }

    let score = Math.trunc(this.registry.get(GameDataHandler.keys.Score));

    this.scoreLabel.text = score+"";
  }

  updateBlocksBroken(tile:Tile)
  {
    if(tile != undefined) {
      let prevBlocks = Math.trunc(this.registry.get(GameDataHandler.keys.BlocksBroken));
      this.registry.inc(GameDataHandler.keys.BlocksBroken, 1);
      let blocks: number = Math.trunc(this.registry.get(GameDataHandler.keys.BlocksBroken));

      let notification: BlocksBrokenNotification = this.getNewBlocksBrokenNotification(tile.x, tile.y);
      notification.show(this.smashBarMeter.parentContainer.x + this.smashBarMeter.x+this.smashBarMeter.displayWidth, this.smashBarMeter.parentContainer.y + this.smashBarMeter.y);

      TazGameReaction.getComponent(this.taz_screen_character).ReactHit();

      let scale = blocks / this.levelManager.TotalTiles;
      console.log("What is scale", scale);
      scale = Math.min(scale, 1);
      this.add.tween({
        targets: this.smashBarMeter,
        scaleX: scale,
        duration: 100,
        delay: 500,
        ease: "Quad.InOut"
      }).on("complete",()=>{
        for(let i = 0; i< this.stars.length; ++i)
        {
          let target = Math.floor(this.level.smashLevels.smashLevels[i]*(this.levelManager.TotalTiles/100));
          if(prevBlocks <target && blocks >= target)
          {
            this.stars[i].tint = 0xffffff;
            this.add.tween({
              targets: this.stars[i],
              scale: 0.75,
              duration: 100,
              ease: "Back.Out"
            })
          }
        }

        let target = Math.floor(this.level.targetScore*(this.levelManager.TotalTiles/100));
        if(prevBlocks <target && blocks >= target)
        {
          this.unlockNextLevel.tint = 0xffffff;
            this.add.tween({
              targets: this.unlockNextLevel,
              scale: 0.75,
              duration: 100,
              ease: "Back.Out"
            })
        }
      })

      if(this.levelManager.destroyedAllTiles() && this.player.velocity > GameConfig.minSpeedBeforeAllowingNewSpin)
      {
        this.time.delayedCall(500, ()=>{this.finish()}, undefined, this);
      }
    }
    else
    {
      this.smashBarMeter.scaleX = 0;
    }
  }

  private scoreLabelTween:any;
  private scoreBoxTween:any;
  private scoreMultiplierTween:any;
  private scoreLabelColor:number = 0;
  private showScoreMultiplier(multiplier:number, showPopup:boolean = true)
  {
    if(this.scoreBoxTween != undefined)
    {
      this.scoreBoxTween.stop();
    }


    if(showPopup)
    {
    this.scoreMultiplierText.x = 960;
    this.scoreMultiplierText.y = 384;
    this.scoreMultiplierText.scale = 1;
    this.scoreMultiplierText.alpha = 1;
    this.scoreMultiplierText.visible = true;

    this.scoreMultiplierText.text = multiplier+this.app.LocaleHandler.getLocalisedTextById("multiplierPostFix");
      if(this.scoreMultiplierTween != undefined)
      {
        this.scoreMultiplierTween.stop();
      }
      this.scoreMultiplierTween = this.add.tween({
        targets: this.scoreMultiplierText,
        scale:0.15,
        x:this.spinMeter.x + this.scoreLabelBox.x + this.scoreMultiplierSmallLabel.x,
        y:this.spinMeter.y + this.scoreLabelBox.y + this.scoreMultiplierSmallLabel.y,
        duration:300,
        delay:500,
        ease:Phaser.Math.Easing.Quadratic.InOut
      }).on("complete",()=>{
        this.scoreMultiplierSmallLabel.visible = true;
        this.scoreMultiplierText.visible = false;
      });
    }
    else
    {
      this.scoreMultiplierSmallLabel.visible = true;
    }

    this.scoreMultiplierSmallLabel.text = multiplier+this.app.LocaleHandler.getLocalisedTextById("multiplierPostFix")

    this.scoreLabelBox.scale = 1;
    this.scoreLabel.tint = 0x00e5cb;

    this.scoreLabelColor = this.scoreLabel.tint;
    this.scoreLabel.tint = 0xffff00;

    this.scoreBoxTween = this.add.tween({
      targets:this.scoreLabelBox,
      scale:1.3,
      duration:250,
      loop:-1,
      yoyo:true,    
      ease:Phaser.Math.Easing.Quadratic.InOut
    });
  }

  private getNewScoreNotification(x:number,y:number)
  {
    for(let i = 0; i < this.scoreNotifications.length; ++i)
    {
      if(this.scoreNotifications[i].visible)
      {
        continue;
      }
      let notification = this.scoreNotifications[i];
      notification.x = x;
      notification.y = y;
      return notification;
    }

    let notification = new ScoreMultiplierNotification(this,x,y);
    this.add.existing(notification);
    this.scoreNotifications.push(notification);

    return notification;
  }

  private getNewBlocksBrokenNotification(x:number,y:number)
  {
    for(let i = 0; i < this.blocksBrokenNotification.length; ++i)
    {
      if(this.blocksBrokenNotification[i].visible)
      {
        continue;
      }
      let notification = this.blocksBrokenNotification[i];
      notification.x = x;
      notification.y = y;
      return notification;
    }

    let notification = new BlocksBrokenNotification(this,x,y);
    this.add.existing(notification);
    this.blocksBrokenNotification.push(notification);

    return notification;
  }

  removeEvents()
  {
    this.events.off(Phaser.Scenes.Events.DESTROY, this.destroy, this);
    this.gameUI.events.off(Phaser.Scenes.Events.CREATE, this.gameUI.showGameUI, this.gameUI);
    this.events.off(LevelManager.events.UpdateScore,this.updateScore,this);
    this.events.off(LevelManager.events.BlockBroken,this.updateBlocksBroken,this);
    this.powerupButton.off(UIButton.events.clicked,this.runPowerup,this);

    if(this.input.keyboard != undefined && this.input.keyboard != null)
    {
      this.input.keyboard.off('keydown-SPACE',this.bumperButtonPressed,this);
      this.input.keyboard.off('keydown-SPACE',this.bumperButtonReleased,this);
      this.input.keyboard.off('keydown-F',this.runPowerup,this);
    }

    this.bumperButton.off(UIButton.events.pressed,this.bumperButtonPressed,this);
    this.bumperButton.off(UIButton.events.clicked,this.bumperButtonReleased,this);

    this.events.off(Taz.events.MovementComplete,this.PlayerMovementEnded,this);
    this.events.off(Taz.events.SpeedBoost,this.PlayerSpeedBoost,this);

    this.events.off(Tutorial.events.pausableTutorialStepStarted,this.pauseForTutorial,this);
    this.events.off(Tutorial.events.tutorialStepCompleted,this.tutorialStepCompleted,this);
    this.events.off(Tutorial.events.idlePrompt,this.idlePrompt,this);
  }

  public shutdown() {

    // this.player.stop();

    if (this?.gameUI?.scene?.isActive() || this?.gameUI?.scene?.isPaused()) this.gameUI.scene.stop();

    this.removeEvents();
    this?.parallaxController?.clearLayers();

    for(let i = 0; i < this.scoreNotifications.length; ++i)
    {
      this.scoreNotifications[i].destroy(true);
    }
    this.scoreNotifications = [];
    for(let i = 0; i < this.blocksBrokenNotification.length; ++i)
    {
      this.blocksBrokenNotification[i].destroy(true);
    }
    this.blocksBrokenNotification = [];

    if(this.currentPowerup != undefined)
    {
      this.currentPowerup.destroy();
      this.currentPowerup = undefined;
    }

    super.shutdown();
  }

  public destroy() {
    super.destroy();
  }

  public pause() {
    this.scene.pause();
  }

  public resume() {
    this.scene.resume();
  }

  public back() {
    this.app.App.gotoNextScene(this, TransitionPoint.GameBack, this.sceneData);
  }

  private hideGameUI()
  {
    this.gameUI.hideGameUI();
    this.add.tween({
      targets: this.spinMeter,
      y: "-=500",
      duration: 500,
      ease: "Quad.InOut"
    });

    this.add.tween({
      targets: this.smashBar,
      y: "-=1000",
      duration: 500,
      ease: "Quad.InOut"
    });

    this.add.tween({
      targets: this.teammate,
      y: "+=200",
      duration: 500,
      ease: "Quad.InOut"
    });

    this.add.tween({
      targets: this.bumperButton,
      y: "+=200",
      duration: 500,
      ease: "Quad.InOut"
    });

    this.add.tween({
      targets: this.teammate_sprite,
      y: this.teammate_sprite.y + (this.teammate_sprite.height*2),
      duration: 200,
      ease: "Quad.InOut"
    });

    this.add.tween({
      targets: this.taz_screen_character,
      y: this.taz_screen_character.y + this.taz_screen_character.height,
      duration: 200,
      ease: "Quad.InOut"
    });
  }

  protected finish() {

    this.player.stop();

    this.hideGameUI();

    let score = Math.trunc(this.registry.get(GameDataHandler.keys.Score));
    let highScore = GameDataHandler.getHighScore(this, this.runSettings.selectedLevel);
    let earnedPoints = this.registry.get(GameDataHandler.keys.EarnedPoints);

    if (score > highScore) GameDataHandler.setHighScore(this, this.runSettings.selectedLevel, score);
    this.registry.inc(GameDataHandler.keys.EarnedPoints, score);

    let blocksBroken = Math.trunc(this.registry.get(GameDataHandler.keys.BlocksBroken));
    let starsEarned = LevelUtil.calculateStarRating(this.level, blocksBroken,this.levelManager.TotalTiles);
    GameDataHandler.setStarScore(this, this.runSettings.selectedLevel, starsEarned);

    let target = Math.floor(this.level.targetScore*(this.levelManager.TotalTiles/100));
    // this.gameUI.showEndUI();
    console.log("What is target score ", target, blocksBroken);
    if(blocksBroken >= target)
    {
      if(this.runSettings.selectedLevel != undefined) {
        let nextLevel = LevelUtil.getNextLevelToUnlock(this.runSettings.selectedLevel);
        console.log("What is next level",nextLevel);
        if(nextLevel != undefined) {
          GameDataHandler.unlockLevel(this, nextLevel);
        }
        this.gameUI.showEndScreen(score, highScore, earnedPoints, starsEarned);
      }
    }
    else
    {
      this.gameUI.showFailedScreen();
    }

  }

  /* END-USER-CODE */
}

/* END OF COMPILED CODE */
